// @ts-check
/* ═══════════════════════ VISION — "Spør om det du ser" ═══════════════════════
   Tar et foto/bilde → sender til Claude vision via /api/chat → streamer svar.
   ═══════════════════════════════════════════════════════════════════════════ */

import { compressImage } from './utils.js';
import { haptic } from './utils.js';

const VISION_SYSTEM = `Du er en kunnskapsrik reiseguide for en familie på reise i Canada og Alaska sommeren 2026. Brukeren har tatt et bilde og lurer på hva det viser.

Svar kort og engasjerende på norsk:
- Hva er det? (fjell, dyr, fugl, byggverk, plante, mat, gatekunst, …)
- 2–3 interessante fakta som er relevante for en reisende
- Eventuelle praktiske tips (best tid å se, fotopunkt, lokal historie)

Hvis bildet er uklart eller du er usikker — si det rett ut og foreslå hva brukeren kan se etter.
Vær konsis: maks 6–8 setninger total.`;

/** Konverter Blob → base64 (uten data:-prefix). */
function blobToBase64(blob){
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = String(r.result || '');
      const i = s.indexOf(',');
      resolve(i >= 0 ? s.slice(i + 1) : s);
    };
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

/**
 * Send et bilde + (valgfritt) spørsmål til Claude vision og stream svaret.
 * @param {Blob|File} imageBlob
 * @param {string|undefined} userPrompt
 * @param {(textChunk:string)=>void} [onChunk] - kalt med hver tekst-bit som strommer inn
 * @returns {Promise<string>} fullt svar
 */
export async function askVision(imageBlob, userPrompt, onChunk){
  // Komprimer til ~1024px JPEG før sending — sparer API-tid og kost
  const compressed = await compressImage(imageBlob, 1024, 0.82).catch(() => imageBlob);
  const b64 = await blobToBase64(compressed);
  const mediaType = compressed.type || 'image/jpeg';

  const messages = [{
    role: 'user',
    content: [
      { type: 'image', source: { type: 'base64', media_type: mediaType, data: b64 } },
      { type: 'text', text: userPrompt?.trim() || 'Hva er dette? Fortell meg om det.' }
    ]
  }];

  const res = await fetch('/api/chat', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages, system: VISION_SYSTEM })
  });

  if (!res.ok) {
    const err = await res.text();
    throw new Error('Vision-API feilet: ' + err.slice(0, 200));
  }

  // Stream parsing — server-sent events fra Anthropic
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let full = '';
  let buf = '';

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const lines = buf.split('\n');
    buf = lines.pop() || '';
    for (const line of lines) {
      const t = line.trim();
      if (!t.startsWith('data:')) continue;
      const payload = t.slice(5).trim();
      if (!payload || payload === '[DONE]') continue;
      try {
        const evt = JSON.parse(payload);
        if (evt.type === 'content_block_delta' && evt.delta?.type === 'text_delta') {
          full += evt.delta.text;
          onChunk?.(evt.delta.text);
        }
      } catch {}
    }
  }
  return full;
}

/* ── UI ─────────────────────────────────────────────────────────────── */

let _currentBlob = null;

function modalHTML(){
  return `<div class="vision-modal-bg" id="vision-modal" onclick="if(event.target===this)closeVision()">
    <div class="vision-modal">
      <div class="vision-head">
        <div class="vision-title">✨ Spør om det du ser</div>
        <button class="vision-x" onclick="closeVision()" aria-label="Lukk">×</button>
      </div>
      <div class="vision-body" id="vision-body">
        <div class="vision-empty">
          <div class="vision-empty-em">📸</div>
          <div class="vision-empty-h">Ta et bilde — eller velg fra galleriet</div>
          <div class="vision-empty-s">AI forteller deg hva det er: fjell, fugl, blomst, byggverk, mat …</div>
          <div class="vision-pick-row">
            <label class="vision-pick vision-pick-cam">
              <input type="file" accept="image/*" capture="environment" onchange="visionPicked(this)" hidden>
              <span>📷</span> Ta bilde
            </label>
            <label class="vision-pick">
              <input type="file" accept="image/*" onchange="visionPicked(this)" hidden>
              <span>🖼️</span> Fra galleri
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>`;
}

function ensureModal(){
  let el = document.getElementById('vision-modal');
  if (!el) {
    document.body.insertAdjacentHTML('beforeend', modalHTML());
    el = document.getElementById('vision-modal');
  }
  return el;
}

export function openVision(){
  ensureModal().classList.add('open');
  haptic('light');
  document.body.style.overflow = 'hidden';
}

export function closeVision(){
  const el = document.getElementById('vision-modal');
  if (el) el.classList.remove('open');
  document.body.style.overflow = '';
  _currentBlob = null;
}

async function onPicked(input){
  const file = input.files?.[0];
  if (!file) return;
  _currentBlob = file;
  const body = document.getElementById('vision-body');
  if (!body) return;

  const url = URL.createObjectURL(file);
  body.innerHTML = `
    <div class="vision-preview">
      <img src="${url}" alt="Valgt bilde" class="vision-img">
    </div>
    <div class="vision-prompt-row">
      <input id="vision-prompt" class="vision-input" placeholder="Hva lurer du på? (valgfritt)" autocomplete="off">
      <button class="vision-go" onclick="visionAsk()">Spør AI ✨</button>
    </div>
    <div class="vision-answer" id="vision-answer"></div>
  `;
  setTimeout(() => document.getElementById('vision-prompt')?.focus(), 50);
}

async function ask(){
  if (!_currentBlob) return;
  const promptEl = /** @type {HTMLInputElement|null} */ (document.getElementById('vision-prompt'));
  const ansEl = document.getElementById('vision-answer');
  const userPrompt = promptEl?.value?.trim() || '';
  if (!ansEl) return;

  haptic('medium');
  ansEl.innerHTML = '<div class="vision-loading"><span class="dot"></span><span class="dot"></span><span class="dot"></span> Ser på bildet …</div>';

  let buf = '';
  try {
    await askVision(_currentBlob, userPrompt, (chunk) => {
      buf += chunk;
      ansEl.textContent = buf;
      ansEl.scrollTop = ansEl.scrollHeight;
    });
    haptic('success');
  } catch (e) {
    ansEl.innerHTML = `<div class="vision-err">⚠️ ${e.message}</div>`;
    haptic('error');
  }
}

// Eksponer for inline onclick
if (typeof window !== 'undefined') {
  window.openVision = openVision;
  window.closeVision = closeVision;
  window.visionPicked = onPicked;
  window.visionAsk = ask;
}
